/**
 * Admin schemas for the PromptAssistant LLM-backed wizard (Story 3.18).
 *
 * The wizard converts non-technical operator input (objetivo, tom, restrições,
 * exemplos) into a candidate `systemPrompt` plus a short rationale. The
 * backend route consumes these schemas and forwards the structured input to
 * `AIService.chatCompletion` with the meta-prompt template lived in
 * `apps/api/src/services/prompt-assistant/meta-prompt.ts`.
 *
 * **Constitution Article IV (No Invention):** every field below is required
 * by Story 3.18 §AC #1 / §AC #6 / §AC #9. The `BannedPatternInGeneration`
 * error envelope matches the 422 contract called out by the AC for the safety
 * validator that screens the LLM output.
 */
import { Type } from '@sinclair/typebox';
/**
 * Tone enum mirrors the wizard radio group on step 1 (frontend). Backed by a
 * union of literals so the meta-prompt builder can switch on it explicitly
 * without runtime mapping code.
 */
export const AssistantTone = Type.Union([
    Type.Literal('formal'),
    Type.Literal('casual'),
    Type.Literal('amigavel'),
    Type.Literal('direto'),
], { $id: 'AssistantTone' });
export const ASSISTANT_TONES = [
    'formal',
    'casual',
    'amigavel',
    'direto',
];
/**
 * Inline Q/A example collected on step 3. Each pair is at most ~200 chars
 * (no Hard limit on backend — the meta-prompt prunes if the total context
 * grows past the model's window).
 */
export const AssistantExample = Type.Object({
    q: Type.String({ minLength: 1, maxLength: 500 }),
    a: Type.String({ minLength: 1, maxLength: 500 }),
});
/**
 * Hard ceiling on the number of examples (story §AC #1: `≤5`). Larger inputs
 * are rejected at the schema layer with a 400 from Fastify's typebox-validator.
 */
export const ASSISTANT_MAX_EXAMPLES = 5;
/**
 * Hard ceiling on the number of constraints (story §AC #1 implicit — guard
 * against runaway inputs). 20 is generous for human review.
 */
export const ASSISTANT_MAX_CONSTRAINTS = 20;
/**
 * `POST /admin/prompts/assistant/generate` body.
 *
 * `campaignId` is optional — when present the route loads the active prompt
 * to include as context in the meta-prompt; absent it generates without
 * baseline. Constraints are an unbounded (capped) list of free-text rules.
 */
export const AssistantGenerateBody = Type.Object({
    campaignId: Type.Optional(Type.String({ format: 'uuid' })),
    objective: Type.String({ minLength: 10, maxLength: 1000 }),
    tone: AssistantTone,
    constraints: Type.Array(Type.String({ minLength: 1, maxLength: 500 }), {
        minItems: 0,
        maxItems: ASSISTANT_MAX_CONSTRAINTS,
    }),
    examples: Type.Array(AssistantExample, { minItems: 0, maxItems: ASSISTANT_MAX_EXAMPLES }),
});
/**
 * Token usage echoed back to the UI so the wizard can render the cost signal
 * without a second round-trip. Mirrors `AICompletionUsage` from the AIService.
 */
export const AssistantTokenUsage = Type.Object({
    in: Type.Integer({ minimum: 0 }),
    out: Type.Integer({ minimum: 0 }),
});
/**
 * `POST /admin/prompts/assistant/generate` 200 response.
 *
 * `systemPrompt` is the candidate text (already passed through the banned-
 * pattern safety check on the backend). `rationale` is a short (≤300 chars)
 * explanation of the design choices the model made.
 */
export const AssistantGenerateResponse = Type.Object({
    systemPrompt: Type.String({ minLength: 1 }),
    rationale: Type.String({ minLength: 1 }),
    modelUsed: Type.String({ minLength: 1 }),
    latencyMs: Type.Integer({ minimum: 0 }),
    tokenUsage: AssistantTokenUsage,
});
/**
 * `POST /admin/prompts/assistant/apply` body.
 *
 * Bound to a campaign (required) — the route picks "new prompt" vs "new
 * version" based on whether the campaign already has an active prompt.
 */
export const AssistantApplyBody = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
    systemPrompt: Type.String({ minLength: 1 }),
    rationale: Type.String({ minLength: 1, maxLength: 500 }),
});
/**
 * `POST /admin/prompts/assistant/apply` 200 / 201 response. The route
 * forwards the prompt resource from the underlying create/update; we re-
 * expose it under `prompt` for parity with `/admin/prompts` endpoints.
 */
export const AssistantApplyResponse = Type.Object({
    prompt: Type.Object({
        id: Type.String({ format: 'uuid' }),
        campaignId: Type.String({ format: 'uuid' }),
        campaignName: Type.String(),
        version: Type.Integer({ minimum: 1 }),
        systemPrompt: Type.String({ minLength: 1 }),
        active: Type.Boolean(),
        createdBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
        createdAt: Type.String({ format: 'date-time' }),
        updatedAt: Type.String({ format: 'date-time' }),
    }),
    mode: Type.Union([Type.Literal('new'), Type.Literal('new_version')]),
});
/**
 * 422 envelope returned when the generated output trips the banned-pattern
 * safety check. `offenders` lists every pattern that matched so the wizard
 * can inject them as constraints on the retry (§AC #9).
 */
export const AssistantBannedPatternError = Type.Object({
    error: Type.Object({
        code: Type.Literal('BANNED_PATTERN_IN_GENERATION'),
        message: Type.String(),
        offenders: Type.Array(Type.String({ minLength: 1 })),
        suggestion: Type.Optional(Type.String()),
    }),
});
//# sourceMappingURL=prompt-assistant.js.map